import React from "react";
import Button from "Components/button/Button";
import Checkbox from "Components/checkbox/Checkbox";
import Input from "Components/input/Input";
import PasswordGenerator from "Components/PasswordGenerator";
import WarningBanner from "Components/warningBanner/WarningBanner";
import TranslatedString from "Components/TranslatedString";
import Toggle from "Components/toggle/Toggle";
import DropdownIndicator from "Components/customArrowSelect/CustomArrowSelect";
import Select from "Components/select/Select";
import Repeater from "Components/repeater/Repeater";
import { isValidIPv4CIDR } from "Utils/ip";
import { t } from "i18next";
import cx from "classnames";
import { alertSuccess, alertError } from "Utils/alerts";
import s from "./security.scss";
import request from "@/config.js";
import QboxmailApi from "@/qboxmailApi";
import AvailabilityBox from "Components/availabilityBox/AvailabilityBox";

export default class PasswordForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      viewFields: [
        "password_confirmation",
        "password",
        "block_old_password",
        "password_expiration",
        "password_change_disabled",
        "password_recovery_disabled",
        "force_otp",
        "allow_nets",
        "apply_allow_nets_webmail",
        "api_enabled",
        "destroy_sessions_on_password_expiration",
      ],
      OTPdisabled: false,
      disablingOTP: false,
      resetPasswordForced: props.emailAccount.force_new_password,
      forcingResetPassword: false,
    };
  }

  componentWillUnmount() {
    this.props.resetFields(...this.state.viewFields);
  }

  cancelForcePassword = () => {
    if (this.state.forcingResetPassword) return false;
    this.updateEmailAccount({ force_new_password: false }).then(() => {
      this.setState({ resetPasswordForced: false });
    });
  };

  forceResetPassword = () => {
    this.updateEmailAccount({ force_new_password: true }).then(() => {
      this.setState({ resetPasswordForced: true });
    });
  };

  updateEmailAccount = (body) => {
    this.setState({ forcingResetPassword: true });
    const api = new QboxmailApi({ useCookie: true });
    return api
      .editEmailAccount(this.props.emailAccount.domain_code, this.props.emailAccount.code, body)
      .then(() => {
        this.setState({ forcingResetPassword: false });
        alertSuccess(t("alert.accountUpdatedSuccess"));
      })
      .catch(() => {
        this.setState({ forcingResetPassword: false });
      });
  };

  forceDisableOTP = () => {
    this.setState({ disablingOTP: true });

    request({
      method: "DELETE",
      url: `/users/${this.props.emailAccount.code}/otp`,
    })
      .then(() => {
        this.setState({ disablingOTP: false, OTPdisabled: true });
        alertSuccess(t("alert.OTP.disabled"));
      })
      .catch((e) => {
        this.setState({ disablingOTP: false });
        if (e instanceof Error) throw e;
      });
  };

  fillPasswordFields = (generatedPassword) => {
    this.props.setFormFieldValue({
      password: generatedPassword,
      password_confirmation: generatedPassword,
    });
  };

  render() {
    const {
      emailAccount,
      isFormLoading,
      setFormFieldValue,
      submitForm,
      loggedUser,
      formData,
      formErrors,
      loggedUserType,
      domain,
      domain: {
        plan: { features },
      },
    } = this.props;

    let canUpdatePassword = true;
    let canUpdatePasswordMsg;
    if (emailAccount.password_change_disabled && loggedUser.type === "EmailAccount") {
      canUpdatePassword = false;
      canUpdatePasswordMsg = (
        <div className={cx(s.disabledMsg, "inputFull2")}>{t("infoMsg.passwordReset.disabled")}</div>
      );
    }

    return (
      <form className={s.security} method="post" onSubmit={(e) => submitForm(e, this.state.viewFields)}>
        <div className={s.wrapInfoBtn}>
          <div className={s.warnignMsg}>
            {!formErrors.onForm && !this.props.areFieldsPristine(...this.state.viewFields) && (
              <WarningBanner color="yellow-text" msg={<TranslatedString>infoMsg.unsaveChanged</TranslatedString>} />
            )}
            {formErrors.onForm && <WarningBanner msg={formErrors.onForm} color="red" />}
          </div>

          <div className={s.infoBtnGroup}>
            <Button className={s.docsBtn} docsKey="emailAccount_settings" color="docs" type="button">
              button.docs
            </Button>
            <Button className={s.saveBtn} type="submit" loading={isFormLoading}>
              button.save
            </Button>
          </div>
        </div>
        <div className={s.settings}>
          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.password.title")}</div>
              <div className="sectionDesc">{t("settings.sections.password.desc.emailAccount")}</div>
            </div>
            <div className="formsSectionSP">
              {canUpdatePasswordMsg}

              {loggedUserType !== "EmailAccount" && (
                <Input
                  className="inputFull2"
                  name="password"
                  label={t("input.label.password.new")}
                  type="password"
                  onChange={setFormFieldValue}
                  value={formData.password}
                  errors={formErrors.password}
                  disabled={!canUpdatePassword}
                  autoComplete="off"
                  showPasswordScore
                />
              )}

              {loggedUserType !== "EmailAccount" && (
                <Input
                  className="inputFull2"
                  name="password_confirmation"
                  label={t("input.label.passwordConfirm.new")}
                  disabled={!canUpdatePassword}
                  type="password"
                  onChange={setFormFieldValue}
                  value={formData.password_confirmation}
                  errors={formErrors.password_confirmation}
                />
              )}

              {loggedUserType !== "EmailAccount" && canUpdatePassword && (
                <PasswordGenerator
                  className={cx("inputFull2", { inputAddMargin: true })}
                  onNewPassword={this.fillPasswordFields}
                />
              )}

              <Toggle
                className="inputFull2"
                label={t("input.label.disable_pwd_change")}
                name="password_change_disabled"
                checked={formData.password_change_disabled}
                errors={formErrors.password_change_disabled}
                onChange={setFormFieldValue}
              />

              {features.password_recovery_disabled && (
                <Toggle
                  className="inputFull2"
                  label={t("input.label.disable_pwd_recovery")}
                  name="password_recovery_disabled"
                  checked={formData.password_recovery_disabled}
                  errors={formErrors.password_recovery_disabled}
                  onChange={setFormFieldValue}
                />
              )}

              <AvailabilityBox currentPlan={domain.plan.name} notAvailableFor={["basic", "professional"]}>
                <Toggle
                  className="inputFull2"
                  label={t("input.label.block_old_pwd")}
                  name="block_old_password"
                  checked={formData.block_old_password}
                  errors={formErrors.block_old_password}
                  onChange={setFormFieldValue}
                  disabled={domain.plan.name !== "enterprise"}
                />

                {!this.state.resetPasswordForced && (
                  <div className="inputFull2 wrapInlineBtnSP">
                    <div className={cx("labelInlineBtnSP", { disabled: domain.plan.name !== "enterprise" })}>
                      {t("input.label.password.forceChange.nextLogin")}
                    </div>
                    <Button
                      color="outline"
                      type="button"
                      loading={this.state.forcingResetPassword}
                      onClick={this.forceResetPassword}
                      disabled={domain.plan.name !== "enterprise"}
                    >
                      {"button.force.change"}
                    </Button>
                  </div>
                )}

                {this.state.resetPasswordForced && (
                  <div className={cx("inputFull2", s.disabledMsg)}>
                    <WarningBanner
                      color="blue"
                      msg={<TranslatedString>infoMsg.resetPwdForced</TranslatedString>}
                      info
                    />
                    <span className="internalLink" onClick={this.cancelForcePassword}>
                      {this.state.forcingResetPassword ? "Resetting" : "Reset"}
                    </span>
                  </div>
                )}
              </AvailabilityBox>
            </div>
          </div>

          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.passwordExpiration.title")}</div>
              <div className="sectionDesc">{t("settings.sections.passwordExpiration.emailDesc")}</div>
            </div>
            <div className="formsSectionSP">
              <AvailabilityBox currentPlan={domain.plan.name} notAvailableFor={["basic"]}>
                <Select
                  className={cx("inputFull2", "selectExpiration")}
                  label={t("input.label.pwd_expiration_email")}
                  components={{ DropdownIndicator }}
                  name="password_expiration"
                  onChange={setFormFieldValue}
                  value={parseInt(formData.password_expiration)}
                  errors={formErrors.password_expiration}
                  options={[
                    {
                      label: t("input.label.pwd_expiration.never"),
                      value: -1,
                    },
                    {
                      label: t("input.label.pwd_expiration.3"),
                      value: 3,
                    },
                    {
                      label: t("input.label.pwd_expiration.6"),
                      value: 6,
                    },
                    {
                      label: t("input.label.pwd_expiration.12"),
                      value: 12,
                    },
                  ]}
                  clearable={false}
                  inline={true}
                  disabled={domain.plan.name === "basic"}
                />

                {formData.password_expiration > 0 && (
                  <p className={cx("inputFull2", "sectionDesc")}>
                    {t("input.label.password_expiration_counter_start", { months: formData.password_expiration })}
                  </p>
                )}

                <Toggle
                  className="inputFull2"
                  label={t("input.label.destroy_sessions_on_password_expiration")}
                  name="destroy_sessions_on_password_expiration"
                  checked={formData.destroy_sessions_on_password_expiration}
                  errors={formErrors.destroy_sessions_on_password_expiration}
                  onChange={setFormFieldValue}
                  disabled={domain.plan.name === "basic"}
                />
              </AvailabilityBox>
            </div>
          </div>

          {loggedUserType !== "EmailAccount" && loggedUserType !== "Postmaster" && (
            <div className="sectionSP">
              <div className="infoSectionSP">
                <div className="settingTitle">{t("settings.sections.otp.title")}</div>
                <div className="sectionDesc">{t("settings.sections.otp.desc.emailAccount")}</div>
              </div>
              <div className="formsSectionSP">
                <p className="inputFull2 text-sm leading-5">{t("settings.sections.otp.explaination.emailaccount")}</p>

                {emailAccount.otp && !this.state.OTPdisabled ? (
                  <Button
                    className="inputFull2"
                    color="outline"
                    type="button"
                    loading={this.state.disablingOTP}
                    onClick={this.forceDisableOTP}
                  >
                    {"button.disableOTP"}
                  </Button>
                ) : (
                  <div className="inputFull2">
                    <WarningBanner
                      warning
                      color="yellow"
                      msg={<TranslatedString>infoMsg.OTP.disabled</TranslatedString>}
                    />
                  </div>
                )}

                <AvailabilityBox currentPlan={domain.plan.name} notAvailableFor={["basic", "professional"]}>
                  <Toggle
                    className="inputFull2"
                    label={t("input.label.force_otp")}
                    name="force_otp"
                    checked={formData.force_otp}
                    errors={formErrors.force_otp}
                    onChange={setFormFieldValue}
                    disabled={domain.plan.name !== "enterprise"}
                  />
                </AvailabilityBox>
              </div>
            </div>
          )}

          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.ipEnabled.title")}</div>
              <div className="sectionDesc">
                <TranslatedString>settings.sections.ipEnabled.desc.emailAccount</TranslatedString>
              </div>
            </div>
            <div className="formsSectionSP">
              <Repeater
                className="inputFull2"
                labelInput={t("input.label.add.ipAddress")}
                label={t("listTable.header.title.ipEnabled")}
                values={formData.allow_nets}
                onChange={setFormFieldValue}
                maxValues={5}
                name="allow_nets"
                errors={formErrors.allow_nets}
                placeholder={t("input.placeholder.ip")}
                validateNewValue={(newVal) => {
                  if (!isValidIPv4CIDR(newVal)) {
                    alert(`${newVal} is not a valid ip address`);
                    return false;
                  }
                  return true;
                }}
              />

              <AvailabilityBox currentPlan={domain.plan.name} notAvailableFor={["basic", "professional"]}>
                <Checkbox
                  className="inputFull2"
                  label={t("input.label.ip.restrictionsToWebmail")}
                  name="apply_allow_nets_webmail"
                  onChange={setFormFieldValue}
                  checked={formData.apply_allow_nets_webmail}
                  errors={formErrors.apply_allow_nets_webmail}
                  disabled={domain.plan.name !== "enterprise"}
                />
              </AvailabilityBox>
            </div>
          </div>

          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.api.title")}</div>
              <div className="sectionDesc">{t("settings.sections.api.desc.emailAccount")}</div>
            </div>
            <div className="formsSectionSP">
              <AvailabilityBox currentPlan={domain.plan.name} notAvailableFor={["basic"]}>
                <Toggle
                  className="inputFull2"
                  label={t("input.label.api_enabled")}
                  name="api_enabled"
                  checked={!!formData.api_enabled}
                  errors={formErrors.api_enabled}
                  onChange={setFormFieldValue}
                  disabled={domain.plan.name === "basic"}
                />
              </AvailabilityBox>
            </div>
          </div>
        </div>
      </form>
    );
  }
}
